/**
 * The side navigation and HandHold's panel share the screen, so they take
 * turns (md+ only; below that the navigation is a drawer):
 *
 *  - HandHold's panel opens      -> collapse the navigation to its icon rail.
 *  - The panel closes            -> expand it again, if HandHold collapsed it.
 *                                   A menu the user collapsed themselves stays
 *                                   collapsed.
 *  - The user expands the menu
 *    while the panel is open     -> put HandHold away. `minimize`, not `close`:
 *                                   close exits the workflow, minimize hides the
 *                                   panel and keeps a guide in progress, which
 *                                   picks up where it was when reopened.
 */

type SdkLike = {
  getState: () => { isOpen?: boolean } | undefined
  subscribe: (listener: () => void) => () => void
  minimize: () => void
}

type NavState = {
  collapsed: boolean
  setCollapsed: (collapsed: boolean) => void
}

type NavStoreLike = {
  getState: () => NavState
  subscribe: (listener: (state: NavState, prev: NavState) => void) => () => void
}

// The collapsed flag is persisted, so "HandHold collapsed this" has to survive
// a reload too — otherwise a reload mid-guide leaves the menu collapsed for
// good for someone who never collapsed it. Session-scoped: a new tab starts
// from the user's own choice.
export const RESTORE_KEY = "handhold.sidenav.restore"

const readRestore = () => {
  try {
    return sessionStorage.getItem(RESTORE_KEY) === "1"
  } catch {
    return false
  }
}

const writeRestore = (restore: boolean) => {
  try {
    if (restore) sessionStorage.setItem(RESTORE_KEY, "1")
    else sessionStorage.removeItem(RESTORE_KEY)
  } catch {
    // storage unavailable: the handoff still works for this page view
  }
}

export const isDesktopViewport = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(min-width: 768px)").matches

/** Links the two; returns the function that unlinks them. */
export const linkSideNavToHandhold = (
  sdk: SdkLike,
  nav: NavStoreLike,
  isDesktop: () => boolean = isDesktopViewport
): (() => void) => {
  let panelOpen = false
  // Set while HandHold itself changes the menu, so its own collapse/expand is
  // never read as the user expanding it.
  let changingMenu = false

  const setMenuCollapsed = (collapsed: boolean) => {
    changingMenu = true
    try {
      nav.getState().setCollapsed(collapsed)
    } finally {
      changingMenu = false
    }
  }

  const onPanelChange = () => {
    const open = Boolean(sdk.getState()?.isOpen)
    if (open === panelOpen) return
    panelOpen = open
    if (!isDesktop()) return

    if (open) {
      if (!nav.getState().collapsed) {
        writeRestore(true)
        setMenuCollapsed(true)
      }
    } else if (readRestore()) {
      writeRestore(false)
      if (nav.getState().collapsed) setMenuCollapsed(false)
    }
  }

  const onMenuChange = (state: NavState, prev: NavState) => {
    if (changingMenu || !isDesktop()) return
    if (prev.collapsed && !state.collapsed && panelOpen) {
      // The menu is already where the user wants it; nothing to restore later.
      writeRestore(false)
      sdk.minimize()
    }
  }

  const unsubscribePanel = sdk.subscribe(onPanelChange)
  const unsubscribeMenu = nav.subscribe(onMenuChange)
  // The panel may already be open: a guide restored on page load.
  onPanelChange()

  return () => {
    unsubscribePanel()
    unsubscribeMenu()
  }
}
